import { Users } from 'lucide-react';
import { useMentees } from '../lib/data';
import type { Mentee, MenteeTrail } from '../lib/api';
import SiteHeader from './SiteHeader';

export default function MyShagird() {
  const { mentees, loading } = useMentees();

  return (
    <div className="min-h-screen bg-paper-50 dark:bg-clay-floor">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <div className="animate-rise-in">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-terracotta-500" />
            <h1 className="text-xl font-semibold text-ink-900 dark:text-paper-50">My Shagird</h1>
          </div>
          <p className="mt-0.5 text-sm text-ink-500 dark:text-paper-300">
            Track your mentees' progress across their assigned kushtis.
          </p>
        </div>

        <section className="mt-6">
          {loading ? (
            <div className="py-10 text-center text-sm text-ink-400">Loading your shagird…</div>
          ) : mentees.length === 0 ? (
            <div className="rounded-xl border border-dashed border-rule p-8 text-center text-sm text-ink-400 bg-paper-100/50 dark:border-clay-rule dark:bg-clay-surface/60">
              No shagird assigned to you yet.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {mentees.map((mentee) => (
                <MenteeCard key={mentee.uid} mentee={mentee} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function MenteeCard({ mentee }: { mentee: Mentee }) {
  return (
    <div className="rounded-xl border border-rule bg-paper-0 p-4 shadow-sm dark:border-clay-rule dark:bg-clay-surface">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-verdant-700 text-[11px] font-semibold text-white">
          {mentee.initials}
        </span>
        <div className="min-w-0">
          <p className="font-semibold text-ink-900 dark:text-paper-50">{mentee.displayName}</p>
          <p className="text-xs text-ink-400">
            {mentee.assignedCount} kushti{mentee.assignedCount !== 1 ? 's' : ''} assigned
          </p>
        </div>
      </div>

      {mentee.trails.length > 0 && (
        <div className="mt-3 flex flex-col gap-2">
          {mentee.trails.map((trail) => (
            <TrailRow key={trail.trackId} trail={trail} />
          ))}
        </div>
      )}
    </div>
  );
}

function TrailRow({ trail }: { trail: MenteeTrail }) {
  const pct =
    trail.totalSteps > 0
      ? Math.round((trail.completedSteps / trail.totalSteps) * 100)
      : 0;
  const complete = trail.status === 'completed' || (trail.totalSteps > 0 && trail.completedSteps >= trail.totalSteps);

  return (
    <div className="flex flex-col gap-1 rounded-lg border border-rule bg-paper-50 px-3 py-2.5 dark:border-clay-rule dark:bg-clay-floor">
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-sm font-medium text-ink-900 dark:text-paper-50">{trail.title}</p>
        <span className="shrink-0 text-xs font-medium text-ink-400">
          {complete ? 'Complete' : `${trail.completedSteps}/${trail.totalSteps}`}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-paper-200 dark:bg-clay-raised">
        <div
          className={`h-full rounded-full transition-all duration-500 ${complete ? 'bg-verdant-500' : 'bg-terracotta-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
